// Per-site distribution price overrides for the bakery module.
//
// Pricing resolution order (highest priority first):
//   1. Site-specific override in this store (per-site, per-product)
//   2. Branch price from productPricing.js (dept → limpopo/pretoria)
//   3. Legacy unitPrice fallback (inside getProductPrice)
//
// Records are keyed by deterministic id {dept}_{siteId}_{productCode} so
// re-saves overwrite the same Firestore doc (no duplicates). Historical
// distribution records are unaffected because saveDistributionBatch
// freezes unitPrice at save time — editing a site override only changes
// the price for NEW records.

import * as Sentry from '@sentry/react'
import { restSet, restDelete, restQuery } from './firestoreRest'
import { getProductPrice } from './productPricing'

const COLLECTION = 'bakeryDistributionPrices'
const LS_PREFIX = 'desres_bakery_dist_prices_'

function lsKey(dept) { return `${LS_PREFIX}${dept}` }

function lsLoad(dept) {
  try { return JSON.parse(localStorage.getItem(lsKey(dept)) || '[]') }
  catch { return [] }
}

function lsSave(dept, list) {
  try { localStorage.setItem(lsKey(dept), JSON.stringify(list)) }
  catch { /* quota / serialization — ignore */ }
}

function upsertLocal(dept, record) {
  const list = lsLoad(dept)
  const idx = list.findIndex(r => r.id === record.id)
  if (idx >= 0) list[idx] = record
  else list.push(record)
  lsSave(dept, list)
}

function removeLocal(dept, id) {
  const list = lsLoad(dept).filter(r => r.id !== id)
  lsSave(dept, list)
}

export function priceDocId(dept, siteId, productCode) {
  return `${String(dept || '').toLowerCase()}_${String(siteId || '').toLowerCase()}_${String(productCode || '').toUpperCase()}`
}

// ─── Reads ──────────────────────────────────────────────────────────

/** Sync read of all overrides for a dept. Returns whatever is cached
 *  locally. Call refreshBakeryDistributionPrices(dept) after login /
 *  date switch to hydrate from Firestore. */
export function getBakeryDistributionPrices(dept) {
  return lsLoad(dept)
}

/** Async pull from Firestore. Hard-replaces the local cache so stale
 *  overrides from another device don't linger. */
export async function refreshBakeryDistributionPrices(dept) {
  try {
    const docs = await restQuery(COLLECTION, 'departmentId', String(dept))
    const list = Array.isArray(docs) ? docs : []
    lsSave(dept, list)
    return list
  } catch (err) {
    console.warn('[bakeryPricingStore] refresh failed:', err?.message)
    Sentry.captureException(err, { extra: { fn: 'refreshBakeryDistributionPrices', dept } })
    return lsLoad(dept)
  }
}

/** Return the price to charge for one product at one site. Resolution:
 *   1. Local override (if present and > 0)
 *   2. Branch price via getProductPrice(product, dept)
 *   3. Zero if no resolution possible. */
export function getSiteProductPrice(dept, siteId, productCode, products) {
  if (!dept || !siteId || !productCode) return 0
  const overrides = lsLoad(dept)
  const id = priceDocId(dept, siteId, productCode)
  const override = overrides.find(o => o.id === id)
  if (override) {
    const n = Number(override.price)
    if (Number.isFinite(n) && n > 0) return n
  }
  const product = Array.isArray(products)
    ? products.find(p => String(p.code).toUpperCase() === String(productCode).toUpperCase())
    : null
  const branchPrice = getProductPrice(product, dept)
  return Number(branchPrice) || 0
}

/** Build a { productCode: effectivePrice } map for every product in the
 *  given list, at the given site. Used by DistributionForm to render
 *  "@ R{price}" next to every product without re-calling the resolver. */
export function getSitePriceMap(dept, siteId, products) {
  const map = {}
  for (const p of (products || [])) {
    if (!p?.code) continue
    map[p.code] = getSiteProductPrice(dept, siteId, p.code, products)
  }
  return map
}

/** True if this product at this site has a custom override (as opposed
 *  to falling back to the base/branch price). */
export function isSiteOverride(dept, siteId, productCode) {
  if (!dept || !siteId || !productCode) return false
  const list = lsLoad(dept)
  const id = priceDocId(dept, siteId, productCode)
  const entry = list.find(o => o.id === id)
  if (!entry) return false
  const n = Number(entry.price)
  return Number.isFinite(n) && n > 0
}

// ─── Writes ─────────────────────────────────────────────────────────

/** Create or update a site-specific price override. Local cache updates
 *  immediately (sync UI); Firestore write is awaited so errors surface
 *  to the caller (the admin UI wants to toast on failure). */
export async function setSiteProductPrice(dept, siteId, siteName, productCode, price, adminEmail) {
  if (!dept) throw new Error('dept is required')
  if (!siteId) throw new Error('siteId is required')
  if (!productCode) throw new Error('productCode is required')
  const n = Number(price)
  if (!Number.isFinite(n) || n < 0) throw new Error('price must be a non-negative number')

  const id = priceDocId(dept, siteId, productCode)
  const existing = lsLoad(dept).find(o => o.id === id) || null
  const today = new Date().toISOString().slice(0, 10)
  const record = {
    id,
    departmentId: dept,
    siteId,
    siteName: siteName || '',
    productCode: String(productCode).toUpperCase(),
    price: n,
    effectiveFrom: today,
    setBy: adminEmail || 'unknown',
    createdAt: existing?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  upsertLocal(dept, record)
  try {
    await restSet(`${COLLECTION}/${id}`, record)
  } catch (err) {
    console.warn('[bakeryPricingStore] setSiteProductPrice failed:', err?.message)
    Sentry.captureException(err, { extra: { fn: 'setSiteProductPrice', id, dept, siteId, productCode } })
    throw err
  }
  return record
}

/** Remove a site override. Future reads fall back to the base/branch
 *  price via getSiteProductPrice. Historical distribution docs are NOT
 *  affected — their frozen unitPrice remains. */
export async function removeSiteProductPrice(dept, siteId, productCode) {
  const id = priceDocId(dept, siteId, productCode)
  removeLocal(dept, id)
  try {
    await restDelete(`${COLLECTION}/${id}`)
  } catch (err) {
    console.warn('[bakeryPricingStore] removeSiteProductPrice failed:', err?.message)
    Sentry.captureException(err, { extra: { fn: 'removeSiteProductPrice', id, dept, siteId, productCode } })
    throw err
  }
  return true
}

/** Copy every override from one site to another. Useful when two sites
 *  share a pricing policy. Returns the count of overrides copied. Skips
 *  overrides that already exist on the destination (won't clobber). */
export async function copySitePrices(dept, fromSiteId, toSiteId, toSiteName, adminEmail) {
  if (fromSiteId === toSiteId) return { copied: 0, skipped: 0 }
  const all = lsLoad(dept)
  const source = all.filter(o => o.siteId === fromSiteId)
  const existingDest = new Set(all.filter(o => o.siteId === toSiteId).map(o => o.productCode))
  let copied = 0
  let skipped = 0
  for (const src of source) {
    if (existingDest.has(src.productCode)) { skipped++; continue }
    await setSiteProductPrice(dept, toSiteId, toSiteName, src.productCode, src.price, adminEmail)
    copied++
  }
  return { copied, skipped }
}
